import React, { FC, memo, AriaAttributes } from 'react';

import { propsHelperRem } from '@/shared/lib/propsHelper';

interface TopTenIconProps extends Pick<AriaAttributes, 'aria-hidden'> {
    getMaskClassName: (maskIndex: number) => string;
    shapePathClassName: string;
    rootClassName: string;
}

interface TopTenIconFactoryProps extends TopTenIconProps {
    cardIndex: number;
}

interface NumberIconSvgProps extends TopTenIconProps {
    numId: string;
    data: NumberIconData;
}

interface ShapePath {
    d: string;
    fillRule?: 'evenodd';
}

interface MaskPath {
    d: string;
    strokeWidth?: number;
    strokeDashoffset: string;
    strokeDasharray: string;
    strokeLinejoin?: 'bevel' | 'miter' | 'round';
}

interface NumberIconData {
    shapePaths: ShapePath[];
    maskPaths: MaskPath[];
}

const NUMBER_1 = {
    shapePaths: [
        {
            d: 'M126.146,48L163.35,48L128.011,148L41.7966,148L32,176L239.406,176L248.98,148L157.708,148L202.941,20L126.146,20L126.146,48Z',
        },
    ],
    maskPaths: [
        {
            d: 'M126.146,34L183.146,34L136.146,167',
            strokeDashoffset: '594.18',
            strokeDasharray: '198.06',
        },
        {
            d: 'M31.6453,162L249.645,162',
            strokeDashoffset: '654',
            strokeDasharray: '218',
        },
    ],
};

const NUMBER_2 = {
    shapePaths: [
        {
            d: 'M28.1,176L38.5,146.3C43.2,145.6,48,145,53.1,144.3L53.1,144.3C84.5,140,121.6,134.9,154,125.8C175.2,119.9,193,112.6,205.3,103.7C217.3,95,222.5,85.9,222.5,75.5C222.5,66.6,216.7,58.1,201.4,51.3C186.3,44.5,165,41.2,142.9,41.8C120.8,42.4,99.4,47,84.1,54.5C68.4,62.2,62.5,71.1,62.5,79.5L34.5,79.5C34.5,55.4,52.1,39.1,71.7,29.4C91.6,19.6,117.3,14.5,142.1,13.8C166.9,13.1,192.7,16.7,212.8,25.7C232.8,34.6,250.5,50.7,250.5,75.5C250.5,97.7,238.2,114.4,221.7,126.4C209.3,135.4,193.7,142.4,177,148L256,148L246.4,176L28.1,176Z',
        },
    ],
    maskPaths: [
        {
            d: 'M48.5,79.5C48.5,14.5,236.5,8,236.5,75.5C236.5,140.7,96,151.5,28,162L257.1,162',
            strokeDashoffset: '2108.34',
            strokeDasharray: '702.78',
        },
    ],
};

const NUMBER_3 = {
    shapePaths: [
        {
            d: 'M33.9999,48L43.7965,20L254.145,20L254.145,58.4088L228.718,64.8185C232.096,65.9231,235.333,67.2516,238.388,68.8383C245.936,72.7587,252.592,78.3696,257.277,86.1516C261.955,93.921,264.145,102.985,264.145,113C264.145,141.151,243.645,158.855,220.136,168.611C196.451,178.439,166.001,182.124,136.571,181.18C107.08,180.234,76.994,174.587,53.8313,164.421C31.8923,154.792,10.2555,138.222,10.6468,112.785L38.6435,113.215C38.5349,120.278,44.8355,129.895,65.0841,138.782C84.1088,147.132,110.46,152.328,137.469,153.195C164.539,154.063,190.652,150.53,209.404,142.749C228.332,134.895,236.145,124.599,236.145,113C236.145,107.115,234.888,103.249,233.289,100.594C231.699,97.9512,229.249,95.6433,225.482,93.6867C217.406,89.4919,204.619,87.6023,187.541,88.0208C170.845,88.4299,151.987,90.9596,132.966,93.9771C127.173,94.8961,121.315,95.8697,115.515,96.8339L115.511,96.8344C102.692,98.9651,90.1518,101.049,79.2093,102.395L74.0782,74.9247L180.887,48L33.9999,48Z',
        },
    ],
    maskPaths: [
        {
            d: 'M33.6451,34L240.145,34L240.145,47.5L77.5002,88.5C139.834,80.8333,250.145,49.4,250.145,113C250.145,192.5,23.6451,178,24.6451,113',
            strokeDashoffset: '2567.4',
            strokeDasharray: '855.8',
        },
    ],
};

const NUMBER_4 = {
    shapePaths: [
        {
            d: 'M48.2,20L77.9,20L59.2,73.5L200.3,73.5L218.9,20L248.5,20L229.9,73.5L270.2,73.5L260.6,101.5L220.1,101.5L194.3,176L164.7,176L190.6,101.5L19.8,101.5L48.2,20Z',
        },
    ],
    maskPaths: [
        {
            d: 'M66,11.5L39.5,87.5L271,87.5',
            strokeDashoffset: '935.97',
            strokeDasharray: '311.99',
        },
        {
            d: 'M236.5,12L176.5,184.5',
            strokeDashoffset: '547.92',
            strokeDasharray: '182.64',
        },
    ],
};

const NUMBER_5 = {
    shapePaths: [
        {
            d: 'M188.271,20L178.697,48L96.2998,48L89.6758,66.1818C118.265,62.4879,153.826,58.2221,184.601,58.9413C204.321,59.4022,223.933,61.9013,239.068,69.0531C246.812,72.7127,253.951,77.8538,259.15,85.0961C264.456,92.4865,267.149,101.249,267.149,111C267.149,139.151,246.649,156.855,223.139,166.611C199.454,176.439,169.005,180.124,139.575,179.18C110.084,178.234,79.9974,172.587,56.8347,162.421C34.8958,152.792,13.2589,136.222,13.6502,110.785L41.6469,111.215C41.5383,118.278,47.8389,127.895,68.0875,136.782C87.1123,145.132,113.463,150.328,140.472,151.195C167.542,152.063,193.655,148.53,212.408,140.749C231.336,132.895,239.149,122.599,239.149,111C239.149,106.626,238.024,103.68,236.405,101.425C234.68,99.0225,231.787,96.5816,227.105,94.3688C217.385,89.7758,202.56,87.3687,183.947,86.9337C152.982,86.2101,116.673,90.9212,86.9298,94.7803C81.1263,95.5333,75.5726,96.2539,70.3576,96.8953L47.6696,99.6858L76.7003,20L188.271,20Z',
        },
    ],
    maskPaths: [
        {
            d: 'M189,34L86.5,34L68.6486,83C130.982,75.3333,253.149,54.5,253.149,111C253.149,190.5,26.6486,176,27.6486,111',
            strokeDashoffset: '1893.99',
            strokeDasharray: '631.33',
        },
    ],
};

const NUMBER_6 = {
    shapePaths: [
        {
            d: 'M170.406,14.5142C170.411,14.514,170.415,14.5138,171,28.5016C171.585,42.4893,171.589,42.4892,171.591,42.4891L171.566,42.4902C171.533,42.4917,171.476,42.4944,171.394,42.4986C171.23,42.5068,170.968,42.5207,170.613,42.5418C169.902,42.5838,168.822,42.6543,167.415,42.765C164.6,42.9865,160.489,43.3679,155.434,44.0019C145.292,45.2737,131.501,47.5429,116.811,51.5156C102.357,55.4243,87.9494,60.7381,75.3732,67.8061C103.937,62.8271,137.022,58.4295,167.265,57.7179C191.938,57.1374,216.262,58.9487,234.927,65.8826C244.378,69.394,253.237,74.5297,259.802,82.1486C266.597,90.0345,270.25,99.8068,270.25,111.002C270.25,140.317,245.411,157.969,219.662,167.364C192.847,177.149,158.55,180.774,126.289,178.916C94.2736,177.072,61.931,169.691,39.9223,155.451C28.7811,148.242,19.2617,138.596,14.712,126.007C10.0305,113.053,11.3515,99.0404,18.1327,84.9354L18.3326,84.5197L18.5594,84.1181C37.8782,49.9038,77.8846,33.0366,109.502,24.4865C125.78,20.0843,140.911,17.6038,151.949,16.2195C157.484,15.5255,162.027,15.1024,165.219,14.8513C166.816,14.7257,168.077,14.6429,168.957,14.5907C169.397,14.5647,169.743,14.5462,169.987,14.5339C170.11,14.5278,170.207,14.5231,170.278,14.5198L170.366,14.5159L170.406,14.5142ZM41.0271,103.065C73.6042,95.9834,124.369,86.735,167.923,85.7102C191.594,85.1532,211.598,87.0857,225.176,92.1299C231.845,94.6075,236.086,97.5195,238.59,100.426C240.865,103.066,242.25,106.296,242.25,111.002C242.25,121.437,233.434,132.534,210.065,141.061C187.763,149.198,157.481,152.666,127.899,150.962C98.0704,149.244,71.3036,142.406,55.1327,131.943C47.184,126.8,42.8461,121.473,41.0452,116.49C39.7907,113.019,39.3861,108.678,41.0271,103.065Z',
            fillRule: 'evenodd',
        },
    ],
    maskPaths: [
        {
            d: 'M171,28.5015C171,28.5015,63.5002,33,30.7502,91.0015C-13.0001,182.001,256.25,190.501,256.25,111.001C256.25,47.6474,96.2448,76.2778,31.5,90.8326',
            strokeDashoffset: '2106.33',
            strokeDasharray: '702.11',
        },
    ],
};

const NUMBER_7 = {
    shapePaths: [
        {
            d: 'M29.3,48L39.0967,20L263.788,20L239.267,89.4758L230.508,90.4186L230.505,90.4189L230.498,90.4196L230.458,90.4241C230.41,90.4295,230.329,90.4388,230.215,90.4521C229.986,90.4788,229.628,90.5219,229.148,90.5829C228.187,90.7049,226.742,90.8987,224.88,91.1766C221.154,91.7329,215.779,92.6242,209.31,93.9495C196.313,96.612,179.174,100.971,162.192,107.752C126.769,121.896,99.0001,143.524,99.0001,176L71.0001,176C71.0001,124.476,115.231,96.3539,151.808,81.7482C170.826,74.1542,189.687,69.388,203.69,66.5192C209.601,65.3084,214.695,64.4257,218.634,63.8071L224.213,48L29.3,48Z',
            fillRule: 'evenodd',
        },
    ],
    maskPaths: [
        {
            d: 'M24.0001,34L244,34L229,76.5C229,76.5,85.0002,92,85.0002,176',
            strokeDashoffset: '1365.72',
            strokeDasharray: '455.24',
        },
    ],
};

const NUMBER_8 = {
    shapePaths: [
        {
            d: 'M110.9,26C125.9,19.2,144.9,15,164.5,14.3C184.1,13.5,202,16.3,215,22C221.5,24.8,227.4,28.6,231.5,33.6C235.7,38.7,238.5,45.6,237.2,54C235.9,62.5,230.8,69.7,224.9,75.1C222.9,76.9,220.8,78.6,218.6,80.2C221.2,81,223.8,81.8,226.2,82.7C236,86.4,244.6,91.2,250.6,97.4C256.6,103.6,260.9,112.2,259.2,123C257.5,133.8,250.5,142.8,242.4,149.6C234.3,156.4,224.1,161.9,213,166.4C190.7,175.4,161.9,181.3,131.6,182.5C101.3,183.7,74,180,54.3,172.6C44.5,168.9,35.9,164.1,29.9,157.9C23.9,151.7,19.6,143.1,21.3,132.3C23,121.5,30,112.5,38.1,105.7C46.2,98.9,56.4,93.4,67.5,88.9C73.2,86.6,79.3,84.5,85.7,82.6C85,81.9,84.4,81.3,83.8,80.5C79.6,75.4,76.8,68.5,78.1,60.1C79.4,51.6,84.5,44.4,90.4,39C96.2,33.7,103.4,29.4,110.9,26ZM153,72.7C151.6,72.7,150.3,72.8,148.9,72.8C148.7,72.8,148.5,72.8,148.3,72.8C123,73.3,104.3,68,105.4,60.2C106.6,51.6,130.9,43.3,159.9,41.5C188.8,39.7,211.3,45.2,210.1,53.8C208.9,62.4,184.6,70.7,155.6,72.5C154.7,72.7,153.9,72.7,153,72.7ZM231.9,123.2C229.7,138.3,186.8,152.5,136.2,155C85.6,157.5,46.4,147.3,48.6,132.2C50.8,117.1,93.7,102.9,144.3,100.4C194.9,97.9,234.1,108.1,231.9,123.2Z',
            fillRule: 'evenodd',
        },
    ],
    maskPaths: [
        {
            d: 'M162.3,28.1C126.1,29.4,94.7,45.7,92.2,61.7C89.7,77.7,119.6,87.9,155.8,86.5L155.8,86.5C209,86.1,248.6,100.2,245.2,121.6C241.6,144.2,191.7,166.4,133.7,168.7C75.7,171,31.6,156.5,35.2,133.8C38.8,111.2,88.7,88.9,146.7,86.7L146.7,86.7C182.9,85.3,220.9,69.8,223.2,52.5C225.9,35.6,198.2,26.8,162.3,28.1Z',
            strokeDashoffset: '2370.78',
            strokeDasharray: '790.26',
        },
    ],
};

const NUMBER_9 = {
    shapePaths: [
        {
            d: 'M249.8,116.1L203,195.1L179,180.9L206.4,134.6C178.6,141.9,144.2,147.2,112.7,147.9C88.5,148.4,64.4,146.1,45.8,138.8C36.4,135.1,27.6,129.7,21.1,122C14.4,114.1,10.7,104.3,10.7,93C10.7,64.4,34.4,45.1,59.8,33.5C86.2,21.5,120,14.9,152.1,14.1C183.9,13.2,216.2,17.9,238.7,30.4C250.1,36.8,260,45.8,265,58.2C270.1,70.9,269.2,85,263.1,99.4L237.3,88.5C241.2,79.2,240.8,73,239,68.6C237.1,64,232.9,59.2,225,54.8C208.9,45.8,182.4,41.2,152.8,42C123.5,42.8,93.5,48.8,71.3,58.9C48.2,69.6,38.7,81.9,38.7,93C38.7,97.7,40.1,101.1,42.5,103.9C45.1,107,49.4,110.1,56.1,112.7C69.6,118,89.3,120.3,112.1,119.9C157.7,119,208.8,107.3,231.7,96.4L249.8,116.1Z',
            fillRule: 'evenodd',
        },
    ],
    maskPaths: [
        {
            d: 'M250.2,94C290.2,-1,24.7,13.5,24.7,93C24.7,156.6,187.5,133,237.7,109L191,188',
            strokeLinejoin: 'bevel',
            strokeDashoffset: '1875.06',
            strokeDasharray: '625.02',
        },
    ],
};

const NUMBER_10 = {
    shapePaths: [
        {
            d: 'M191.7,14C142.7,15.9,95.4,54.2,87.9,101.5C80.4,148.8,116.1,183.8,165.1,181.9C214.1,180,261.4,141.7,268.9,94.4C276.4,47.1,240.7,12.1,191.7,14ZM115.9,100.4C120.7,70.3,151.8,43.1,187.3,41.7C222.8,40.3,245.7,65.4,240.9,95.5C236.1,125.6,205,152.8,169.5,154.2C134,155.6,111.2,130.5,115.9,100.4Z',
            fillRule: 'evenodd',
        },
        {
            d: 'M58.4,48L41.1,48L41.1,20L97.8,20L43.4,176L13.7,176L58.4,48Z',
        },
    ],
    maskPaths: [
        {
            d: 'M41.1,34L78.1,34L26.6,181.5',
            strokeDashoffset: '579.69',
            strokeDasharray: '193.23',
        },
        {
            d: 'M189.5,27.8C147.3,29.5,108,62.2,101.9,100.9C95.8,139.6,125,169.7,167.3,168C209.5,166.3,248.8,133.6,254.9,94.9C261.1,56.2,231.8,26.2,189.5,27.8',
            strokeWidth: 32,
            strokeDashoffset: '1387.8',
            strokeDasharray: '462.6',
        },
    ],
};

const NUMBER_ICON_DATA: NumberIconData[] = [
    NUMBER_1,
    NUMBER_2,
    NUMBER_3,
    NUMBER_4,
    NUMBER_5,
    NUMBER_6,
    NUMBER_7,
    NUMBER_8,
    NUMBER_9,
    NUMBER_10,
];

const NumberIconSvg: FC<NumberIconSvgProps> = ({
    rootClassName,
    shapePathClassName,
    getMaskClassName,
    numId,
    data,
    ...restProps
}) => {
    const maskId = `num_${numId}-masks`;

    return (
        <svg
            {...restProps}
            {...propsHelperRem(11.3, 8.1, {}, '0 0 280 196')}
            shapeRendering="geometricPrecision"
            textRendering="geometricPrecision"
            className={rootClassName}
        >
            <defs>
                <linearGradient id="gradient_normal" x1="0.8" x2="0" y1="1.2" y2="0">
                    <stop offset="1.68%" stopColor="#000000" />
                    <stop offset="19.76%" stopColor="#2F2C30" />
                    <stop offset="26.99%" stopColor="#423C43" />
                    <stop offset="34.32%" stopColor="#5D595F" />
                    <stop offset="46.44%" stopColor="#9C9C9C" />
                    <stop offset="53.66%" stopColor="#D2C9D4" />
                    <stop offset="59.74%" stopColor="#E6E6E6" />
                    <stop offset="64.98%" stopColor="#DBDBDB" />
                    <stop offset="73.35%" stopColor="#BDB8BA" />
                    <stop offset="77.09%" stopColor="#B9B2B5" />
                    <stop offset="87.31%" stopColor="#D6CCD6" />
                    <stop offset="92.69%" stopColor="#E2DBE1" />
                    <stop offset="98.15%" stopColor="#D0C4C8" />
                </linearGradient>
                <linearGradient id="gradient_active" x1="0.8" x2="0" y1="1.2" y2="0">
                    <stop offset="-1.21%" stopColor="#858CF0" />
                    <stop offset="6.32%" stopColor="#75C3F0" />
                    <stop offset="11.63%" stopColor="#6EDBF0" />
                    <stop offset="18.09%" stopColor="#7BACF0" />
                    <stop offset="24.2%" stopColor="#848CF0" />
                    <stop offset="33.17%" stopColor="#C28DF8" />
                    <stop offset="39.59%" stopColor="#BAA5BE" />
                    <stop offset="46.58%" stopColor="#C6BAC9" />
                    <stop offset="54.28%" stopColor="#EBE7EC" />
                    <stop offset="60.78%" stopColor="#FFFFFF" />
                    <stop offset="66.37%" stopColor="#EAEAEA" />
                    <stop offset="75.3%" stopColor="#BDB8BA" />
                    <stop offset="79.29%" stopColor="#B9B2B5" />
                    <stop offset="90.2%" stopColor="#F2DFF2" />
                    <stop offset="95.95%" stopColor="#E6D6E4" />
                    <stop offset="101.78%" stopColor="#D0C4C8" />
                </linearGradient>
            </defs>
            <g mask={`url(#${maskId})`}>
                {data.shapePaths.map((sp, i) => (
                    <path
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        d={sp.d}
                        fill="url(#gradient_normal)"
                        fillRule={sp.fillRule}
                        stroke="none"
                        strokeWidth="1"
                    />
                ))}
                {/* eslint-disable-next-line react/no-unknown-property */}
                <mask id={maskId} mask-type="luminance">
                    {data.maskPaths.map((mp, i) => (
                        <path
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            d={mp.d}
                            fill="none"
                            stroke="rgb(255,255,255)"
                            strokeWidth={mp.strokeWidth ?? 28}
                            strokeDashoffset={mp.strokeDashoffset}
                            strokeDasharray={mp.strokeDasharray}
                            strokeLinejoin={mp.strokeLinejoin}
                            className={getMaskClassName(i)}
                        />
                    ))}
                </mask>
            </g>
            <g>
                {data.shapePaths.map((sp, i) => (
                    <path
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        d={sp.d}
                        fill="url(#gradient_active)"
                        fillRule={sp.fillRule}
                        stroke="none"
                        strokeWidth="1"
                        className={shapePathClassName}
                    />
                ))}
            </g>
        </svg>
    );
};

export const AnimatedTopNumberIcon: FC<TopTenIconFactoryProps> = memo(
    ({ cardIndex, ...iconProps }) => {
        const data = NUMBER_ICON_DATA[cardIndex];

        if (!data) {
            return null;
        }

        return (
            <NumberIconSvg data={data} numId={String(cardIndex + 1)} {...iconProps} aria-hidden />
        );
    }
);
